/**
 * Builder for creating veterancy and cohesion modifier maps from unit trait effects
 * All values are extracted from NDF data - no hardcoded fallbacks
 */

import type { UnitTraitEffectsMap } from '../types/unit-trait-effects';
import type { VeterancyModifier, CohesionModifier, TraitModifier } from '../types/veterancy-cohesion';
import { Veterancy, Cohesion } from '../types/veterancy-cohesion';
import { parseVeterancyModifier, parseCohesionModifier, parseTraitModifier } from './modifier-parser';

/**
 * Mapping from veterancy levels to enum-style keys in unit-trait-effects.json
 * Each entry maps a Veterancy enum value to the effect key(s) that provide its modifiers
 * Keys are now decoupled from NDF naming conventions
 */
const VETERANCY_EFFECT_MAPPING: {
  [key in Veterancy]: string[];
} = {
  [Veterancy.VET_0]: ['VET_0'],
  [Veterancy.VET_1]: ['VET_1'],
  [Veterancy.VET_2]: ['VET_2'],
  [Veterancy.VET_3]: ['VET_3'],
  [Veterancy.ARTY_VET_0]: ['ARTY_VET_0'],
  [Veterancy.ARTY_VET_1]: ['ARTY_VET_1'],
  [Veterancy.ARTY_VET_2]: ['ARTY_VET_2'],
  [Veterancy.ARTY_VET_3]: ['ARTY_VET_3'],
  [Veterancy.PLANE_VET_0]: ['PLANE_VET_0'],
  [Veterancy.PLANE_VET_1]: ['PLANE_VET_1'],
  [Veterancy.PLANE_VET_2]: ['PLANE_VET_2'],
  [Veterancy.PLANE_VET_3]: ['PLANE_VET_3'],
  [Veterancy.HELICOPTER_VET_0]: ['HELICOPTER_VET_0'],
  [Veterancy.HELICOPTER_VET_1]: ['HELICOPTER_VET_1'],
  [Veterancy.HELICOPTER_VET_2]: ['HELICOPTER_VET_2'],
  [Veterancy.HELICOPTER_VET_3]: ['HELICOPTER_VET_3'],
  [Veterancy.SF_VET_1]: ['SF_VET_1'],
  [Veterancy.SF_VET_2]: ['SF_VET_2'],
  [Veterancy.SF_VET_3]: ['SF_VET_3'],
};

/**
 * Build veterancy modifiers map from unit trait effects
 * Each veterancy level extracts its values independently from NDF data based on the mapping above
 */
export function buildVeterancyModifiersMap(
  effectsMap: UnitTraitEffectsMap
): { [key in Veterancy]: VeterancyModifier } {
  const modifiers: Partial<{ [key in Veterancy]: VeterancyModifier }> = {};

  // Build modifiers for each veterancy level based on the mapping
  for (const [vet, effectNames] of Object.entries(VETERANCY_EFFECT_MAPPING) as [
    Veterancy,
    string[]
  ][]) {
    // Skip levels with no effects
    if (effectNames.length === 0) {
      continue;
    }

    // Collect all effects for this veterancy level
    const effects = effectNames
      .map((name) => effectsMap[name])
      .filter((effect): effect is NonNullable<typeof effect> => effect !== undefined);

    if (effects.length === 0) {
      continue;
    }

    // Parse modifiers from all effects
    const allParsedModifiers: Partial<VeterancyModifier>[] = [];
    for (const effect of effects) {
      const parsed = parseVeterancyModifier(effect.effects);
      allParsedModifiers.push(parsed);
    }

    // Combine modifiers (later effects override earlier ones for same properties)
    const combinedModifier: Partial<VeterancyModifier> = {
      veterancy: vet,
    };

    for (const parsed of allParsedModifiers) {
      Object.assign(combinedModifier, parsed);
    }

    modifiers[vet] = combinedModifier as VeterancyModifier;
  }

  // Fill in any missing veterancies with empty modifiers (no fallback values)
  const allVeterancies: Veterancy[] = [
    Veterancy.VET_0,
    Veterancy.VET_1,
    Veterancy.VET_2,
    Veterancy.VET_3,
    Veterancy.ARTY_VET_0,
    Veterancy.ARTY_VET_1,
    Veterancy.ARTY_VET_2,
    Veterancy.ARTY_VET_3,
    Veterancy.PLANE_VET_0,
    Veterancy.PLANE_VET_1,
    Veterancy.PLANE_VET_2,
    Veterancy.PLANE_VET_3,
    Veterancy.HELICOPTER_VET_0,
    Veterancy.HELICOPTER_VET_1,
    Veterancy.HELICOPTER_VET_2,
    Veterancy.HELICOPTER_VET_3,
    Veterancy.SF_VET_1,
    Veterancy.SF_VET_2,
    Veterancy.SF_VET_3,
  ];

  for (const vet of allVeterancies) {
    if (!modifiers[vet]) {
      modifiers[vet] = {
        veterancy: vet,
      };
    }
  }

  return modifiers as { [key in Veterancy]: VeterancyModifier };
}

/**
 * Build cohesion modifiers map from unit trait effects
 * All values extracted from NDF - no hardcoded fallbacks
 */
export function buildCohesionModifiersMap(
  effectsMap: UnitTraitEffectsMap
): { [key in Cohesion]: CohesionModifier } {
  const modifiers: Partial<{ [key in Cohesion]: CohesionModifier }> = {};

  // Ground unit cohesions
  const groundHigh = effectsMap['HIGH'];
  if (groundHigh) {
    const parsed = parseCohesionModifier(groundHigh.effects);
    // Parser always returns accuracy (defaults to 1 if no precision modifier)
    modifiers[Cohesion.HIGH] = {
      ...parsed,
      cohesion: Cohesion.HIGH,
      accuracy: parsed.accuracy ?? 1,
      reloadTime: parsed.reloadTime ?? 1,
      aimTime: parsed.aimTime ?? 1,
    };
  }

  const groundNormal = effectsMap['NORMAL'];
  if (groundNormal) {
    const parsed = parseCohesionModifier(groundNormal.effects);
    // Parser always returns accuracy
    modifiers[Cohesion.NORMAL] = {
      ...parsed,
      cohesion: Cohesion.NORMAL,
      accuracy: parsed.accuracy ?? 1,
    };
  }

  const groundMediocre = effectsMap['MEDIOCRE'];
  if (groundMediocre) {
    const parsed = parseCohesionModifier(groundMediocre.effects);
    // Parser always returns accuracy
    modifiers[Cohesion.MEDIOCRE] = {
      ...parsed,
      cohesion: Cohesion.MEDIOCRE,
      accuracy: parsed.accuracy ?? 1,
    };
  }

  const groundLow = effectsMap['LOW'];
  if (groundLow) {
    const parsed = parseCohesionModifier(groundLow.effects);
    // Parser always returns accuracy
    modifiers[Cohesion.LOW] = {
      ...parsed,
      cohesion: Cohesion.LOW,
      accuracy: parsed.accuracy ?? 1,
    };
  }

  // Artillery unit cohesions
  const artyHigh = effectsMap['ARTY_HIGH'];
  if (artyHigh) {
    const parsed = parseCohesionModifier(artyHigh.effects);
    // Parser always returns accuracy (defaults to 1 if no precision modifier)
    modifiers[Cohesion.ARTY_HIGH] = {
      ...parsed,
      cohesion: Cohesion.ARTY_HIGH,
      accuracy: parsed.accuracy ?? 1,
      reloadTime: parsed.reloadTime ?? 1,
      aimTime: parsed.aimTime ?? 1,
    };
  }

  const artyNormal = effectsMap['ARTY_NORMAL'];
  if (artyNormal) {
    const parsed = parseCohesionModifier(artyNormal.effects);
    // Parser always returns accuracy
    modifiers[Cohesion.ARTY_NORMAL] = {
      ...parsed,
      cohesion: Cohesion.ARTY_NORMAL,
      accuracy: parsed.accuracy ?? 1,
    };
  }

  const artyMediocre = effectsMap['ARTY_MEDIOCRE'];
  if (artyMediocre) {
    const parsed = parseCohesionModifier(artyMediocre.effects);
    // Parser always returns accuracy
    modifiers[Cohesion.ARTY_MEDIOCRE] = {
      ...parsed,
      cohesion: Cohesion.ARTY_MEDIOCRE,
      accuracy: parsed.accuracy ?? 1,
    };
  }

  const artyLow = effectsMap['ARTY_LOW'];
  if (artyLow) {
    const parsed = parseCohesionModifier(artyLow.effects);
    // Parser always returns accuracy
    modifiers[Cohesion.ARTY_LOW] = {
      ...parsed,
      cohesion: Cohesion.ARTY_LOW,
      accuracy: parsed.accuracy ?? 1,
    };
  }

  // Air unit cohesions (planes)
  // HIGH means no penalty - no effect in NDF
  modifiers[Cohesion.PLANE_HIGH] = {
    cohesion: Cohesion.PLANE_HIGH,
    accuracy: 1,
    reloadTime: 1,
    aimTime: 1,
  };

  const airNormal = effectsMap['PLANE_NORMAL'];
  if (airNormal) {
    const parsed = parseCohesionModifier(airNormal.effects);
    // Parser always returns accuracy
    modifiers[Cohesion.PLANE_NORMAL] = {
      ...parsed,
      cohesion: Cohesion.PLANE_NORMAL,
      accuracy: parsed.accuracy ?? 1,
    };
  }

  const airMediocre = effectsMap['PLANE_MEDIOCRE'];
  if (airMediocre) {
    const parsed = parseCohesionModifier(airMediocre.effects);
    // Parser always returns accuracy
    modifiers[Cohesion.PLANE_MEDIOCRE] = {
      ...parsed,
      cohesion: Cohesion.PLANE_MEDIOCRE,
      accuracy: parsed.accuracy ?? 1,
    };
  }

  const airLow = effectsMap['PLANE_LOW'];
  if (airLow) {
    const parsed = parseCohesionModifier(airLow.effects);
    // Parser always returns accuracy
    modifiers[Cohesion.PLANE_LOW] = {
      ...parsed,
      cohesion: Cohesion.PLANE_LOW,
      accuracy: parsed.accuracy ?? 1,
    };
  }

  // Fill in any missing cohesions with default HIGH values (no penalty)
  const allCohesions: Cohesion[] = [
    Cohesion.HIGH,
    Cohesion.NORMAL,
    Cohesion.MEDIOCRE,
    Cohesion.LOW,
    Cohesion.ARTY_HIGH,
    Cohesion.ARTY_NORMAL,
    Cohesion.ARTY_MEDIOCRE,
    Cohesion.ARTY_LOW,
    Cohesion.PLANE_HIGH,
    Cohesion.PLANE_NORMAL,
    Cohesion.PLANE_MEDIOCRE,
    Cohesion.PLANE_LOW,
  ];

  for (const cohesion of allCohesions) {
    if (!modifiers[cohesion]) {
      modifiers[cohesion] = {
        cohesion,
        accuracy: 1,
      };
    }
  }

  return modifiers as { [key in Cohesion]: CohesionModifier };
}

/**
 * Set of veterancy/cohesion keys to exclude from trait modifiers
 */
const VETERANCY_COHESION_KEYS = new Set([
  // Veterancy keys
  'VET_0', 'VET_1', 'VET_2', 'VET_3',
  'ARTY_VET_0', 'ARTY_VET_1', 'ARTY_VET_2', 'ARTY_VET_3',
  'PLANE_VET_0', 'PLANE_VET_1', 'PLANE_VET_2', 'PLANE_VET_3',
  'HELICOPTER_VET_0', 'HELICOPTER_VET_1', 'HELICOPTER_VET_2', 'HELICOPTER_VET_3',
  'SF_VET_1', 'SF_VET_2', 'SF_VET_3',
  // Cohesion keys
  'HIGH', 'NORMAL', 'MEDIOCRE', 'LOW',
  'ARTY_HIGH', 'ARTY_NORMAL', 'ARTY_MEDIOCRE', 'ARTY_LOW',
  'PLANE_NORMAL', 'PLANE_MEDIOCRE', 'PLANE_LOW',
]);

/**
 * Build trait modifiers map from unit trait effects
 * Filters out veterancy/cohesion effects and builds modifiers for all other traits
 */
export function buildTraitModifiersMap(
  effectsMap: UnitTraitEffectsMap
): { [traitName: string]: TraitModifier } {
  const modifiers: { [traitName: string]: TraitModifier } = {};

  // Iterate through all effects in the map
  for (const [traitName, traitEffect] of Object.entries(effectsMap)) {
    // Skip veterancy/cohesion effects
    if (VETERANCY_COHESION_KEYS.has(traitName)) {
      continue;
    }

    // Parse modifiers from the trait's effects
    const parsed = parseTraitModifier(traitEffect.effects);

    // Create the trait modifier with the trait name
    modifiers[traitName] = {
      ...parsed,
      traitName,
    };
  }

  return modifiers;
}
